import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import "./AttendanceCheckInPage.css";

const AttendanceCheckInPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, authLoading } = useAuth();

  const [status, setStatus] = useState("loading"); // loading | success | error | already_checked_in
  const [eventName, setEventName] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const token = searchParams.get("token");
  const hasCalledRef = useRef(false);

  useEffect(() => {
    // Wait for auth to resolve
    if (authLoading) return;

    // If not logged in, redirect to login and come back
    if (!isAuthenticated()) {
      const returnUrl = encodeURIComponent(
        window.location.pathname + window.location.search
      );
      navigate(`/login?redirect=${returnUrl}`, { replace: true });
      return;
    }

    // Prevent double-call from React StrictMode
    if (hasCalledRef.current) return;
    hasCalledRef.current = true;

    // No token in URL
    if (!token) {
      setStatus("error");
      setErrorMessage("Invalid QR code — no token found in the URL.");
      return;
    }

    // Perform the check-in
    const doCheckIn = async () => {
      try {
        const response = await fetch(
          getApiEndpoint("/v1/attendance/check-in"),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user?.jwt}`,
            },
            body: JSON.stringify({ qr_token: token }),
          }
        );

        const data = await response.json();

        if (response.ok) {
          setEventName(data.event?.name || data.event?.title || "the event");
          setStatus("success");
        } else if (response.status === 409) {
          // Already checked in
          setEventName(data.event?.name || data.event?.title || "this event");
          setStatus("already_checked_in");
        } else {
          setStatus("error");
          setErrorMessage(
            data.message || "Something went wrong. Please try again."
          );
        }
      } catch (err) {
        setStatus("error");
        setErrorMessage(
          "Network error. Please check your connection and try again."
        );
      }
    };

    doCheckIn();
  }, [authLoading, isAuthenticated, token, navigate, user]);

  const handleGoHome = () => {
    navigate("/dashboard", { replace: true });
  };

  if (authLoading || status === "loading") {
    return (
      <div className="check-in-page">
        <div className="check-in-card">
          <div className="check-in-spinner" />
          <p className="check-in-loading-text">Checking you in…</p>
        </div>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="check-in-page">
        <div className="check-in-card check-in-card--success">
          <div className="check-in-icon check-in-icon--success">✓</div>
          <h1 className="check-in-title">Checked In!</h1>
          <p className="check-in-subtitle">
            You've been successfully checked in to <strong>{eventName}</strong>.
          </p>
          <button
            className="check-in-btn check-in-btn--primary"
            onClick={handleGoHome}
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (status === "already_checked_in") {
    return (
      <div className="check-in-page">
        <div className="check-in-card check-in-card--success">
          <div className="check-in-icon check-in-icon--success">✓</div>
          <h1 className="check-in-title">Already Checked In</h1>
          <p className="check-in-subtitle">
            You're already checked in to <strong>{eventName}</strong>. See you
            there!
          </p>
          <button
            className="check-in-btn check-in-btn--primary"
            onClick={handleGoHome}
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // error state
  return (
    <div className="check-in-page">
      <div className="check-in-card check-in-card--error">
        <div className="check-in-icon check-in-icon--error">✕</div>
        <h1 className="check-in-title">Check-In Failed</h1>
        <p className="check-in-subtitle">{errorMessage}</p>
        <button
          className="check-in-btn check-in-btn--secondary"
          onClick={handleGoHome}
        >
          Go to Dashboard
        </button>
      </div>
    </div>
  );
};

export default AttendanceCheckInPage;
